/**
 * ResourceCopyMoveDialog — CCM Wave 3 (the cross-scope resource copy/move
 * destination picker). Handoff `ccm-lexide-copy-move-ui-handoff-2026-06-30.md`
 * §1/§3/§5.
 *
 * A lawyer viewing a `sources/` resource picks a DESTINATION SCOPE — another
 * case, or their own org ("Global / General") workspace — to copy or move the
 * resource into. The destination is a SCOPE, not a folder: the resource keeps
 * its canonical `sources/<name>` ref in the target (there is no rename / no
 * folder choice in v1). If a same-named source already exists in the dest it
 * is REPLACED — the picker says so up front so the lawyer isn't surprised
 * (v1 has no collision prompt; flagged in the handback as a possible
 * follow-up).
 *
 * The target list (`buildCopyMoveTargets`) EXCLUDES the current scope (the
 * server rejects a same-workspace copy) and never offers `org→org`. On
 * success the dest tree (and, for a move, the source tree) is invalidated by
 * `useResourceCopyMove`; this component drops the toast.
 */

import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Copy, FileText, FilesIcon, Globe, Loader2, MoveRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCases } from "@/hooks/useCases";
import {
  useResourceCopyMove,
  ResourceCopyMoveError,
  type ResourceCopyMoveInput,
} from "@/hooks/useResourceCopyMove";
import { useToastStore } from "@/stores/useToastStore";
import {
  buildCopyMoveTargets,
  type CopyMoveTarget,
} from "@/lib/copy-move-targets";
import { cn } from "@/lib/utils";

/** The source scope the resource lives in — carried from the active selection
 *  (case) or the org workspace. Drives the `from` address + the
 *  exclude-current-scope rule. */
export type CopyMoveSource =
  | { scope: "case"; clientId: string; matterId: string; caseId: string }
  | { scope: "org" };

interface Props {
  open: boolean;
  onClose: () => void;
  mode: "copy" | "move";
  /** The resource being copied/moved, workspace-relative (e.g.
   *  "sources/contrato.pdf"). Machine ref — used for the op, NEVER shown. */
  path: string;
  /** FRIENDLY display name (title → alias → tag). NEVER the OS filename
   *  (rule 6). This is what the lawyer sees in the identity block. */
  displayName: string;
  /** Optional @tag handle, rendered as a small mono chip beside the name. */
  tag?: string;
  /** The source scope. */
  source: CopyMoveSource;
}

export function ResourceCopyMoveDialog({
  open,
  onClose,
  mode,
  path,
  displayName,
  tag,
  source,
}: Props) {
  const { t } = useTranslation();
  const addToast = useToastStore((s) => s.addToast);
  const mutation = useResourceCopyMove();
  // Load the case list only while the dialog is open (no eager fetch).
  const { data: cases, isLoading: casesLoading } = useCases({ enabled: open });
  const [selected, setSelected] = useState<CopyMoveTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const orgLabel = t("resourceCopyMove.globalLabel");
  const targets = useMemo(
    () =>
      buildCopyMoveTargets(
        cases ?? [],
        source.scope === "org"
          ? { scope: "org" }
          : { scope: "case", caseId: source.caseId },
        orgLabel,
      ),
    [cases, source, orgLabel],
  );

  if (!open) return null;

  const heading =
    mode === "copy"
      ? t("resourceCopyMove.headingCopy")
      : t("resourceCopyMove.headingMove");
  const isBusy = mutation.isPending;
  const noTargets = !casesLoading && targets.length === 0;

  function close() {
    setSelected(null);
    setError(null);
    onClose();
  }

  async function run() {
    if (!selected || isBusy) return;
    setError(null);
    const input: ResourceCopyMoveInput = {
      mode,
      path,
      target: selected,
      source,
    };
    try {
      await mutation.mutateAsync(input);
      addToast({
        text:
          mode === "copy"
            ? t("resourceCopyMove.toastCopied", { dest: selected.title })
            : t("resourceCopyMove.toastMoved", { dest: selected.title }),
        kind: "success",
        autoDismissMs: 4000,
      });
      close();
    } catch (err) {
      // The hook's `noGateway` sentinel carries the bare code as its message;
      // translate it. Server-side RPC errors (`INVALID_PARAMS` / `NOT_FOUND` /
      // `INTERNAL_ERROR`) arrive with a human message we surface verbatim.
      if (err instanceof ResourceCopyMoveError) {
        setError(t("resourceCopyMove.noGateway"));
      } else {
        setError(
          err instanceof Error && err.message
            ? err.message
            : t("resourceCopyMove.failed"),
        );
      }
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-coffee-900/40 dark:bg-coffee-950/50" />
        <Dialog.Content
          data-testid="resource-copy-move-dialog"
          data-mode={mode}
          aria-label={heading}
          // Modal — a click outside must NOT discard the pending destination
          // pick (operator 2026-07-03). Close via Cancel / X / Esc only.
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/4 z-50 w-[30rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-2xl border border-cream-200/70 bg-white p-5 shadow-[0_8px_40px_rgba(28,24,20,0.18)] focus:outline-none dark:border-coffee-700/60 dark:bg-coffee-900"
        >
          <VisuallyHidden>
            <Dialog.Title>{heading}</Dialog.Title>
          </VisuallyHidden>

          {/* Header — mode badge + a distinctive title. */}
          <div className="mb-4 flex items-start justify-between gap-2">
            <h2 className="flex items-center gap-2.5 text-body font-semibold text-coffee-900 dark:text-cream-100">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gold-500/15 text-gold-600 dark:text-gold-400"
                aria-hidden
              >
                {mode === "copy" ? <Copy size={16} /> : <MoveRight size={16} />}
              </span>
              {heading}
            </h2>
            <button
              type="button"
              onClick={close}
              aria-label={t("actions.close")}
              className="shrink-0 rounded-lg p-1 text-cream-500 hover:bg-cream-100/70 hover:text-coffee-700 dark:hover:bg-coffee-800/60 dark:hover:text-cream-200"
            >
              <X size={16} />
            </button>
          </div>

          {/* Identity block — the FRIENDLY name + @tag chip. The OS filename
              and path are NEVER shown here (rule 6). */}
          <div className="mb-4 flex items-center gap-2.5 rounded-xl border border-cream-200/70 bg-cream-50/70 px-3 py-2.5 dark:border-coffee-700/60 dark:bg-coffee-800/40">
            <FileText size={16} className="shrink-0 text-cream-500" aria-hidden />
            <span
              data-testid="resource-copy-move-name"
              className="min-w-0 flex-1 truncate text-body-sm font-medium text-coffee-900 dark:text-cream-100"
            >
              {displayName}
            </span>
            {tag && tag.trim() !== displayName && (
              <span className="shrink-0 truncate rounded-md bg-cream-100 px-1.5 py-0.5 font-mono text-caption text-coffee-500 dark:bg-coffee-800 dark:text-cream-400">
                {tag}
              </span>
            )}
          </div>

          <p className="mb-2 text-caption font-medium uppercase tracking-wide text-cream-500">
            {t("resourceCopyMove.pickLabel")}
          </p>

          {casesLoading && (
            <div className="flex items-center gap-2 py-3 text-body-sm text-cream-500">
              <Loader2 size={14} className="animate-spin" />
              {t("resourceCopyMove.loadingCases")}
            </div>
          )}

          {noTargets && (
            <div
              data-testid="resource-copy-move-empty"
              className="rounded-xl border border-dashed border-cream-300/70 px-3 py-5 text-center text-body-sm text-cream-500 dark:border-coffee-700/60"
            >
              {t("resourceCopyMove.noTargets")}
            </div>
          )}

          {!casesLoading && targets.length > 0 && (
            <ul
              data-testid="resource-copy-move-targets"
              className="flex max-h-64 flex-col gap-1 overflow-auto"
            >
              {targets.map((target) => {
                const isSel =
                  selected?.kind === target.kind &&
                  selected.caseId === target.caseId;
                return (
                  <li key={`${target.kind}:${target.caseId}`}>
                    <button
                      type="button"
                      onClick={() => setSelected(target)}
                      data-testid="resource-copy-move-target"
                      data-target-kind={target.kind}
                      data-target-id={target.caseId}
                      data-selected={isSel ? "true" : undefined}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left text-body-sm transition-colors",
                        isSel
                          ? "border-gold-500/60 bg-gold-500/10 font-medium text-coffee-900 dark:text-cream-100"
                          : "border-cream-200/60 text-coffee-700 hover:bg-cream-100/60 dark:border-coffee-700/60 dark:text-cream-200 dark:hover:bg-coffee-800/40",
                      )}
                    >
                      {target.kind === "org" ? (
                        <Globe size={15} className="shrink-0 text-cream-500" />
                      ) : (
                        <FilesIcon size={15} className="shrink-0 text-cream-500" />
                      )}
                      <span className="flex-1 truncate">{target.title}</span>
                      {target.kind === "case" && target.status && (
                        <span className="shrink-0 text-caption uppercase tracking-wide text-cream-500">
                          {target.status}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {error && (
            <div className="mt-3 rounded-lg bg-red-50 p-2.5 text-body-sm text-red-700 dark:bg-red-950 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2">
            {/* Collision note — a quiet footnote, not an alarm box (v1
                replaces a same-named dest source; handoff §3). */}
            <p
              data-testid="resource-copy-move-collision-note"
              className="min-w-0 flex-1 text-caption leading-snug text-cream-500"
            >
              {t("resourceCopyMove.collisionNote")}
            </p>
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={close}
                disabled={isBusy}
                className="rounded-lg px-3 py-1.5 text-body-sm text-cream-500 hover:bg-cream-100/70 disabled:opacity-50 dark:text-cream-400 dark:hover:bg-coffee-800/60"
              >
                {t("actions.cancel")}
              </button>
              <button
                type="button"
                onClick={run}
                disabled={!selected || isBusy}
                data-testid="resource-copy-move-confirm"
                className={cn(
                  "flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-body-sm font-medium transition-colors disabled:cursor-not-allowed",
                  selected && !isBusy
                    ? "bg-gold-500 text-coffee-950 hover:bg-gold-400"
                    : "bg-cream-200/70 text-cream-500 dark:bg-coffee-800 dark:text-coffee-500",
                )}
              >
                {isBusy ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : mode === "copy" ? (
                  <Copy size={13} />
                ) : (
                  <MoveRight size={13} />
                )}
                {mode === "copy"
                  ? t("resourceCopyMove.confirmCopy")
                  : t("resourceCopyMove.confirmMove")}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
